import { Alert, Box, List, Typography, paletteColors } from '@evaneos/b2b-ui';
import { useIntl } from 'react-intl';
import { QueryStatus } from '@tanstack/react-query';
import InboxIcon from '@evaneos/b2b-ui/icons/InboxOutlined';

import { Notification } from '../../../resources/notifications/types';
import { Loader } from '../../../design-system/Loader';

import { NotificationItem } from './NotificationItem/NotificationItem';

type NotificationContentProps = {
    areNotificationsLoading: boolean;
    notifications: Notification[] | undefined;
    notificationsStatus: QueryStatus;
};

export const NotificationsContent = ({
    areNotificationsLoading,
    notifications = [],
    notificationsStatus,
}: NotificationContentProps) => {
    const { formatMessage } = useIntl();
    const hasNotifications = notificationsStatus === 'success' && notifications.length > 0;

    if (areNotificationsLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                <Loader color={paletteColors.green[700]} />
            </Box>
        );
    }
    if (notificationsStatus === 'error') {
        return (
            <Box p={2}>
                <Alert severity="error">
                    <Typography variant="body1" color="error">
                        {formatMessage({
                            id: 'global.error.failed_fetch',
                        })}
                    </Typography>
                </Alert>
            </Box>
        );
    }
    if (notificationsStatus === 'success' && !hasNotifications) {
        return <NotificationsBlockEmptyState />;
    }

    return (
        <List
            disablePadding
            sx={{
                flex: { xs: '1 1 336px', md: '1 1 0' },
                overflowY: 'auto',
            }}
        >
            {notifications.map((notification) => (
                <NotificationItem key={notification.id} notification={notification} />
            ))}
        </List>
    );
};

const NotificationsBlockEmptyState = () => {
    const { formatMessage } = useIntl();

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                flex: { xs: '0 0 auto', md: 1 },
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.5,
                padding: { xs: 2, md: 3 },
                margin: 'auto',
                minHeight: { xs: '200px', md: 'auto' },
            }}
        >
            <InboxIcon
                htmlColor={paletteColors.green[700]}
                sx={{ fontSize: 32, mb: 0.5 }}
                aria-hidden="true"
            />
            <Typography variant="body1" fontWeight={700}>
                {formatMessage({ id: 'homepage.notifications.empty.title' })}
            </Typography>
            <Typography
                variant="body2"
                color="text.secondary"
                fontWeight={700}
                maxWidth="342px"
                textAlign="center"
            >
                {formatMessage({
                    id: 'homepage.notifications.empty.description',
                })}
            </Typography>
        </Box>
    );
};
